import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { lazy, Suspense } from "react";
import { ROUTES } from "@/config/routes";
import SubscriptionGate from "./components/auth/SubscriptionGate";

// Loading fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
  </div>
);

// Lazy load pages for better code splitting
const Index = lazy(() => import("./pages/Index"));
const Auth = lazy(() => import("./pages/Auth"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Overview = lazy(() => import("./pages/Overview"));
const OperationsHub = lazy(() => import("./pages/OperationsHub"));
const ContainerSetup = lazy(() => import("./pages/ContainerSetup"));
const Inventory = lazy(() => import("./pages/Inventory"));
const Settings = lazy(() => import("./pages/Settings"));
const Subscription = lazy(() => import("./pages/Subscription"));
const TongsDevices = lazy(() => import("./pages/TongsDevices"));
const TongsDuringService = lazy(() => import("./pages/TongsDuringService"));
const TongsSimulator = lazy(() => import("./pages/TongsSimulator"));
const Integrations = lazy(() => import("./pages/Integrations"));
const InstallApp = lazy(() => import("./pages/InstallApp"));
const NotFound = lazy(() => import("./pages/NotFound"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));

// Feature module lazy imports - Import directly from page files
const AICookingPlan = lazy(() => import("./pages/AICookingPlan"));
const AIConfiguration = lazy(() => import("./pages/AIConfiguration"));
const AIInsights = lazy(() => import("./pages/AIInsights"));

const MenuManagement = lazy(() => import("./pages/MenuManagement"));
const MenuPricing = lazy(() => import("./pages/MenuPricing"));
const Recipes = lazy(() => import("./pages/Recipes"));

const BusinessSetup = lazy(() => import("./pages/BusinessSetup"));
const BusinessInsights = lazy(() => import("./pages/BusinessInsights"));
const QualityCompliance = lazy(() => import("./pages/QualityCompliance"));

const BusinessAnalytics = lazy(() => import("./pages/BusinessAnalytics"));
const CostAnalytics = lazy(() => import("./pages/CostAnalytics"));
const CustomerBehaviorAnalytics = lazy(() => import("./pages/CustomerBehaviorAnalytics"));
const PredictiveAnalytics = lazy(() => import("./pages/PredictiveAnalytics"));
const ProfitabilityAnalysis = lazy(() => import("./pages/ProfitabilityAnalysis"));
const StaffAnalytics = lazy(() => import("./pages/StaffAnalytics"));
const SupplyChainAnalytics = lazy(() => import("./pages/SupplyChainAnalytics"));
const SustainabilityAnalytics = lazy(() => import("./pages/SustainabilityAnalytics"));
const TongsAnalytics = lazy(() => import("./pages/TongsAnalytics"));

const queryClient = new QueryClient();

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <div className="overflow-x-hidden max-w-full">
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <SubscriptionGate>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  {/* Public routes */}
                  <Route path={ROUTES.HOME} element={<Index />} />
                  <Route path={ROUTES.AUTH} element={<Auth />} />
                  <Route path={ROUTES.FORGOT_PASSWORD} element={<ForgotPassword />} />
                  <Route path={ROUTES.TERMS} element={<TermsOfService />} />
                  <Route path={ROUTES.PRIVACY} element={<PrivacyPolicy />} />
                  <Route path={ROUTES.INSTALL} element={<InstallApp />} />
                  
                  {/* Dashboard */}
                  <Route path={ROUTES.DASHBOARD} element={<Dashboard />} />
                  <Route path={ROUTES.OVERVIEW} element={<Overview />} />
                  
                  {/* Operations */}
                  <Route path={ROUTES.OPERATIONS_HUB} element={<OperationsHub />} />
                  <Route path={ROUTES.CONTAINER_SETUP} element={<ContainerSetup />} />
                  <Route path={ROUTES.AI_COOKING_PLAN} element={<AICookingPlan />} />
                  
                  {/* Smart Tongs */}
                  <Route path={ROUTES.TONGS_DEVICES} element={<TongsDevices />} />
                  <Route path={ROUTES.TONGS_DURING_SERVICE} element={<TongsDuringService />} />
                  <Route path={ROUTES.TONGS_ANALYTICS} element={<TongsAnalytics />} />
                  <Route path={ROUTES.TONGS_SIMULATOR} element={<TongsSimulator />} />
                  
                  {/* Inventory & Menu */}
                  <Route path={ROUTES.INVENTORY} element={<Inventory />} />
                  <Route path={ROUTES.MENU_MANAGEMENT} element={<MenuManagement />} />
                  <Route path={ROUTES.RECIPES} element={<Recipes />} />
                  <Route path={ROUTES.MENU_PRICING} element={<MenuPricing />} />
                  
                  {/* Analytics & Insights */}
                  <Route path={ROUTES.AI_INSIGHTS} element={<AIInsights />} />
                  <Route path={ROUTES.BUSINESS_ANALYTICS} element={<BusinessAnalytics />} />
                  <Route path={ROUTES.BUSINESS_INSIGHTS} element={<BusinessInsights />} />
                  <Route path={ROUTES.COST_ANALYTICS} element={<CostAnalytics />} />
                  <Route path={ROUTES.PROFITABILITY} element={<ProfitabilityAnalysis />} />
                  <Route path={ROUTES.SUSTAINABILITY} element={<SustainabilityAnalytics />} />
                  <Route path={ROUTES.STAFF_ANALYTICS} element={<StaffAnalytics />} />
                  <Route path={ROUTES.PREDICTIVE_ANALYTICS} element={<PredictiveAnalytics />} />
                  <Route path={ROUTES.SUPPLY_CHAIN} element={<SupplyChainAnalytics />} />
                  <Route path={ROUTES.CUSTOMER_BEHAVIOR} element={<CustomerBehaviorAnalytics />} />
                  
                  {/* Settings & Configuration */}
                  <Route path={ROUTES.BUSINESS_SETUP} element={<BusinessSetup />} />
                  <Route path={ROUTES.AI_CONFIGURATION} element={<AIConfiguration />} />
                  <Route path={ROUTES.QUALITY_COMPLIANCE} element={<QualityCompliance />} />
                  <Route path={ROUTES.INTEGRATIONS} element={<Integrations />} />
                  <Route path={ROUTES.SUBSCRIPTION} element={<Subscription />} />
                  <Route path={ROUTES.SETTINGS} element={<Settings />} />
                  
                  {/* 404 */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </SubscriptionGate>
          </BrowserRouter>
        </div>
      </TooltipProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
